// src/app/top-bar.tsx
// Zone contract Z2 (GlobalBar): the 48px bar over the workspace frame, on the app background with
// no fill of its own. Design log rules (12–13 Oct):
//   - left, a STRUCTURAL breadcrumb: where the screen sits in the app, not the path taken. A
//     top-level page names its section ("Calendar"); the Dashboard, the root, shows none. Inside a
//     patient it always reads "Patients › <name>", whatever tab is open (tabs are views, not
//     levels). Parents are router links.
//   - Back returns on sub-pages (Antonin, 19 Oct: "bring back a back button before the breadcrumbs"):
//     a 32px arrow before the crumbs, the page you came from (history), or the parent crumb when the
//     page was opened directly. Main (top-level) pages still show nothing on the left: no Back, no
//     lone crumb; the page title names them. The history stack (nav-stack.ts) still runs, unused.
//   - right, Help (32px, quiet) then the search (top-bar-search.tsx).
//   - narrow windows: the search shrinks first (to 200), then each crumb truncates; nothing wraps.
// Plain markup rather than the DS BreadcrumbNavigation: that molecule renders the non-last crumbs
// in the muted ink at 400 and routes through an <a> + callback (DS request in SHOWCASE-CHANGELOG.md).
// Looks: src/styles/showcase-bridge.css under `/* shell */` and `/* top bar */`.
import { Fragment } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { usePatient } from '@/patients/services/use-patient';
import { useNavStack } from './nav-stack';
import { useCrumbName } from './crumb-name';
import { TopBarSearch } from './top-bar-search';
import { crumbsFor, sectionOf } from './top-bar-routes';
import { TopBarPractice } from '@/scheduling/components/calendar/top-bar-practice';
import { ArrowLeft, HelpCircle } from 'lucide-react';

/** The last crumb's own name: the open patient's full name (the patient page's own query, cached, no second
 *  request; first, middle and last, as the page header reads it, like Practice), the encounter's tap-time
 *  navigation state, or the name a page hands over (`setCrumbName`: the appointment's patient, a provider's fees). */
function usePatientName(pathname: string, state: unknown): string | null {
  const ownName = useCrumbName();
  const patientId = (
    matchPath('/patients/edit/:patientId', pathname) ?? matchPath('/patients/edit/:patientId/intake/:runId', pathname)
  )?.params.patientId;
  const { data } = usePatient(patientId);
  if (patientId) return [data?.firstName, data?.middleName, data?.lastName].filter(Boolean).join(' ') || null;
  if (
    matchPath('/scheduling/appointment/:appointmentId', pathname) ||
    matchPath('/billing/master-data/provider-fees/:providerId', pathname)
  ) {
    return ownName;
  }
  if (matchPath('/encounter/:appointmentId', pathname)) {
    const nav = state as { patient?: { name?: string } } | null;
    return nav?.patient?.name ?? ownName;
  }
  return null;
}

export function TopBar() {
  const { pathname, state, key } = useLocation();
  const navigate = useNavigate();
  const crumbs = crumbsFor(pathname, usePatientName(pathname, state));
  useNavStack(sectionOf(pathname));
  // A main page is one crumb deep (or none, the Dashboard): it shows nothing here.
  const isMainPage = crumbs.length <= 1;
  // Back: the page this tab came from when there is in-app history ('default' = the first entry);
  // opened directly, the nearest crumb that links (the parent).
  const parent = [...crumbs].reverse().find((c) => c.to);
  const goBack = () => {
    if (key !== 'default') navigate(-1);
    else if (parent?.to) navigate(parent.to, { state: parent.state });
  };

  return (
    <header data-slot="top-bar" className="shell-topbar">
      <div data-slot="top-bar-lead" className="shell-topbar-lead">
        {!isMainPage && (
          <button type="button" aria-label="Back" onClick={goBack} className="shell-topbar-icon mr-2">
            <ArrowLeft className="size-5" aria-hidden="true" />
          </button>
        )}
        {!isMainPage && (
          <nav aria-label="Breadcrumb" className="shell-crumbs">
            <ol>
              {crumbs.map((crumb, i) => (
                <Fragment key={`${crumb.label}-${i}`}>
                  {i > 0 && (
                    <li aria-hidden="true" className="shell-crumb-sep">
                      ›
                    </li>
                  )}
                  <li>
                    {crumb.to ? (
                      <Link to={crumb.to} state={crumb.state}>{crumb.label}</Link>
                    ) : (
                      <span aria-current="page">{crumb.label}</span>
                    )}
                  </li>
                </Fragment>
              ))}
            </ol>
          </nav>
        )}
      </div>
      <div data-slot="top-bar-actions" className="shell-topbar-actions">
        {/* Help (PROD-1189, moved up from the rail): disabled until a help surface exists. */}
        <button type="button" aria-label="Help" disabled className="shell-topbar-icon">
          <HelpCircle className="size-5" aria-hidden="true" />
        </button>
        <TopBarSearch />
        {/* The active practice, top right on every page (Practice app placement and behaviour). */}
        <TopBarPractice />
      </div>
    </header>
  );
}
